// Sums in number boxes (shared/calc.ts): while a sum is typed in a number box ("40/500"), what it comes to shows just
// under it ("= 0.08"), so the result is seen before it's saved. Mounted once (the app's shell, the vendor portal's), it
// follows whichever box has focus: a number box (inputmode decimal or numeric) or one marked data-calc (a number column's
// cell editor). Inline styles over the colour tokens, so the portal's own stylesheet needs nothing for it.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { calc, isFormula } from "../../../shared/calc.ts";

type Hint = { text: string; right: number; top: number };

const isNumberBox = (el: EventTarget | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement && (el.inputMode === "decimal" || el.inputMode === "numeric" || el.dataset.calc !== undefined);

const resultText = (n: number) => `= ${new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 }).format(n)}`;

export function CalcHint() {
  const [hint, setHint] = useState<Hint | null>(null);
  useEffect(() => {
    const show = (e: Event) => {
      const el = e.target;
      if (!isNumberBox(el)) return;
      const n = isFormula(el.value) ? calc(el.value) : null;
      if (n == null) return setHint(null);
      const r = el.getBoundingClientRect();
      // Under the box, its right edges lined up (numbers sit right); above it when there's no room below.
      const below = r.bottom + 26 < window.innerHeight;
      setHint({ text: resultText(n), right: Math.max(4, window.innerWidth - r.right), top: below ? r.bottom + 3 : r.top - 23 });
    };
    const hide = (e: Event) => isNumberBox(e.target) && setHint(null);
    const gone = () => setHint(null);
    document.addEventListener("input", show, true);
    document.addEventListener("focusin", show, true);
    document.addEventListener("focusout", hide, true);
    window.addEventListener("scroll", gone, true);
    window.addEventListener("resize", gone);
    return () => {
      document.removeEventListener("input", show, true);
      document.removeEventListener("focusin", show, true);
      document.removeEventListener("focusout", hide, true);
      window.removeEventListener("scroll", gone, true);
      window.removeEventListener("resize", gone);
    };
  }, []);
  if (!hint) return null;
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        right: hint.right,
        top: hint.top,
        zIndex: 70,
        pointerEvents: "none",
        padding: "1px 6px",
        borderRadius: 4,
        border: "1px solid var(--line)",
        background: "var(--surface)",
        color: "var(--text)",
        boxShadow: "var(--shadow)",
        font: "500 11px/18px ui-monospace, SFMono-Regular, Menlo, monospace",
        whiteSpace: "nowrap",
      }}
    >
      {hint.text}
    </div>,
    document.body,
  );
}
